import React, { useCallback } from 'react';

// Redux
import { useDispatch } from 'react-redux';

// React Router Dom
import { useHistory } from 'react-router-dom';

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardMedia from '@material-ui/core/CardMedia';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';

// MDI
import Icon from '@mdi/react';
import { mdiFormTextboxPassword as Password } from '@mdi/js';

// Icons
import MoreHorizIcon from 'components/Icons/MoreHoriz';
import DeleteIcon from 'components/Icons/Delete';

// Components
import { DialogContentYesOrNo } from 'components';
import { HasPermission } from 'components/HasPermission';

// Actions
import { setOpen } from 'store/ducks/dialogs/actions';
import * as CompaniesActions from 'store/ducks/companies/actions';
import { forgotCompanyRequest } from 'store/ducks/companies/actions';

// Helpers
import { maskCNPJ, maskPhoneDDI } from 'helpers/maskers';

// Storage
import { setHiddenItems, removeHiddenItems } from 'helpers/transportVouchers';

// Contants
import CONST_IMAGE from 'constants/images';
import PATH from 'constants/basePath';

// Types
import { CompanyInterface } from 'store/ducks/companies/types';

// Styles
import useStyles from './companyNavigation.styles';

interface CompanyNavigationProps {
  companyData: CompanyInterface;
  transportVouchers?: boolean;
}

type Props = CompanyNavigationProps;

const CompanyNavigation: React.FC<Props> = ({
  companyData,
  transportVouchers,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const forgotPasswordRequest = useCallback(
    ({ id }: { id: string }) => {
      if (!id) return;

      dispatch(forgotCompanyRequest({ id }));
    },
    [dispatch],
  );

  if (!companyData) {
    return <></>;
  }

  return (
    <div className={classes.root}>
      <Box display='flex' flexDirection='column'>
        <Box flexGrow={0} p={0}>
          <Card className={classes.card}>
            <CardMedia
              alt='Imagem'
              component='img'
              height={200}
              src={CONST_IMAGE.PLACEHOLDER_IMAGES.COMPANY}
            />
          </Card>
        </Box>
        <Box flexGrow={1} p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography color='primary' variant='h6'>
                {companyData.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography color='primary' display='inline'>
                CNPJ:
              </Typography>
              <Typography display='inline'>
                {' '}
                {maskCNPJ(companyData.cnpj)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography color='primary' display='inline'>
                Telefone:
              </Typography>
              <Typography display='inline'>
                {' '}
                {maskPhoneDDI(companyData.phone)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography color='primary' display='inline'>
                E-mail:&nbsp;
              </Typography>
              <Typography display='inline'>{companyData.email}</Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography color='primary' display='inline'>
                Cadastro:
              </Typography>
              <Typography display='inline'>
                {' '}
                {companyData.createdAt &&
                  new Date(companyData.createdAt).toLocaleDateString()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Divider variant='fullWidth' />
        <Box flexGrow={0} p={2}>
          <Box alignContent='center' display='flex' flexDirection='row'>
            <Box>
              <Tooltip placement='top' title='Editar'>
                <IconButton
                  onClick={() => {
                    history.push(
                      `${PATH.COMPANIES.BASE_URL}/editar/${companyData.id}`,
                    );

                    if (transportVouchers) {
                      setHiddenItems(transportVouchers);
                    } else {
                      removeHiddenItems();
                    }
                  }}>
                  <MoreHorizIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box>
              <Tooltip placement='top' title='Deletar'>
                <HasPermission
                  component={
                    <IconButton
                      onClick={() => {
                        dispatch(
                          setOpen({
                            open: true,
                            content: (
                              <DialogContentYesOrNo
                                image={CONST_IMAGE.TRASH_IMAGE_URL}
                                message={
                                  <Typography
                                    className={classes.messageDialogDelete}
                                    variant='body1'>
                                    Tem certeza que deseja deletar a empresa
                                    <span className={classes.infosDialogDelete}>
                                      {' '}
                                      {companyData.name}
                                    </span>
                                    ?
                                  </Typography>
                                }
                                yesOnClick={() => {
                                  dispatch(
                                    CompaniesActions.deleteCompanyRequest({
                                      body: {
                                        companyId: companyData.id,
                                      },
                                    }),
                                  );
                                }}
                              />
                            ),
                          }),
                        );
                      }}>
                      <DeleteIcon />
                    </IconButton>
                  }
                />
              </Tooltip>
            </Box>
            <Box>
              <Tooltip placement='top' title='Resetar senha'>
                <HasPermission
                  component={
                    <IconButton
                      onClick={() => {
                        forgotPasswordRequest({
                          id: companyData.id,
                        });
                      }}>
                      <Icon path={Password} size={1.1} />
                    </IconButton>
                  }
                />
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export { CompanyNavigation };
export default CompanyNavigation;
